import { isParsableDate } from 'dotcom-core/src/helpers/date';
import type {
  BasePrice,
  BaseSavedPrice,
  Maybe,
  ProductDetailPageQuery,
  ProductVariant,
} from '~/graphql';
import { isTruthy } from '~/helpers/predicate';

export type PdpProductInterface = NonNullable<
  NonNullable<ProductDetailPageQuery['productDetailPage']>['product']
>;

export type PdpLinkedProductInterface = NonNullable<
  NonNullable<
    ProductDetailPageQuery['productDetailPage']
  >['linkedProductDetailPages']
>[number]['product'];

export function getVariant(product: PdpProductInterface) {
  const isConfigurableProduct = product.__typename === 'ConfigurableProduct';

  const loyaltyVariant =
    isConfigurableProduct && product.loyaltyVariant
      ? product.loyaltyVariant
      : null;

  const defaultVariant = isConfigurableProduct ? product.defaultVariant : null;

  return loyaltyVariant || defaultVariant || null;
}

export function getLoyaltyVariant(product: PdpProductInterface) {
  const isConfigurableProduct = product.__typename === 'ConfigurableProduct';

  const loyaltyVariant =
    isConfigurableProduct && product.loyaltyVariant
      ? product.loyaltyVariant
      : null;

  return loyaltyVariant;
}

type Props = { variant?: ProductVariant | null; product: PdpProductInterface };

export function getDiscountPrices({ variant, product }: Props) {
  const hasBundlePricing = getHasBundlePricing(product);
  const configurableProduct = product.__typename === 'ConfigurableProduct';
  const isLoyaltyVariant = isTruthy(getLoyaltyVariant(product));

  let previousPrice: Maybe<BasePrice> | undefined;
  let savingsPrice: Maybe<BaseSavedPrice> | undefined;
  let previousPriceMessage:
    | 'regularPrice'
    | 'fullVersionPrice'
    | 'totalPriceSeparately'
    | 'regularUpdatePrice'
    | undefined;

  const isVariantOnSale = isTruthy(variant?.onSale);
  const isProductOnSale = isTruthy(product?.onSale);

  if (
    (isVariantOnSale && !isLoyaltyVariant) ||
    (hasBundlePricing && isProductOnSale)
  ) {
    previousPrice = variant?.price?.regular;
    savingsPrice = variant?.price?.savings?.regularToFinal;
    previousPriceMessage = 'regularPrice';
  }

  if (configurableProduct && isVariantOnSale && isLoyaltyVariant) {
    previousPrice = variant?.price?.regular;
    savingsPrice = variant?.price?.savings?.regularToFinal;
    previousPriceMessage = 'regularUpdatePrice';
  }

  if (configurableProduct && isLoyaltyVariant && !isVariantOnSale) {
    previousPrice = product?.price?.regular;
    savingsPrice = product?.price?.savings?.regularToLoyalty;
    previousPriceMessage = 'fullVersionPrice';
  }

  if (hasBundlePricing && !isVariantOnSale && !isProductOnSale) {
    previousPrice = product?.price?.totalBundle;
    savingsPrice = product?.price?.savings?.totalBundleToFinal;
    previousPriceMessage = 'totalPriceSeparately';
  }

  return { previousPrice, savingsPrice, previousPriceMessage };
}

export function getReleaseDate({
  product,
  variant,
}: {
  product: PdpProductInterface;
  variant: ProductVariant | undefined | null;
}): Date | null {
  if (variant && isParsableDate(variant.releaseDate)) {
    return new Date(variant.releaseDate);
  }

  if (!variant && isParsableDate(product.releaseDate)) {
    return new Date(product.releaseDate);
  }

  return null;
}

export function getStockStatus({ product, variant }: Props): string {
  // We only consider stockStatus property from variants and BundleProducts.
  const isBundleProduct = product.__typename === 'BundleProduct';

  if (!variant && isBundleProduct) {
    return product.stockStatus;
  }

  if (variant) {
    return variant.stockStatus;
  }

  return 'IN_STOCK';
}

export function getIsShippable({ product, variant }: Props): boolean {
  // Only variants are shippable together with products with BundleProduct type.
  // SimpleProducts/VirtualProducts are part of ConfigurableProducts, so we
  // handle them through variants (defaultVariant).
  const isProductTypeShippable = product.__typename === 'BundleProduct';

  if (!variant && isProductTypeShippable) {
    return product.shippable;
  }

  if (variant) {
    return variant?.shippable;
  }

  return false;
}

// Needed since some ConfigurableProducts are acting as BundleProducts
export function getHasBundlePricing(product: PdpProductInterface): boolean {
  const isBundleProduct = product.__typename === 'BundleProduct';

  const hasBundlePricing =
    isTruthy(product.price?.savings?.totalBundleToFinal?.value) &&
    product.price?.savings?.totalBundleToFinal?.value > 0;

  return isBundleProduct || hasBundlePricing;
}

export function getLinkedProductsForSelector(
  product: PdpProductInterface,
  linkedProductDetailPages?: {
    slug: string;
    product: PdpLinkedProductInterface;
  }[],
) {
  if (!linkedProductDetailPages?.length) {
    return [];
  }
  return [
    {
      slug: product.slug,
      name: product.name,
      description: product.description,
      shortDescription: product.shortDescription,
      currency: product.price?.final.currency,
      value: product.price?.final.value || 0,
    },
    ...(linkedProductDetailPages ?? []).map(({ slug, product }) => ({
      slug,
      name: product.name,
      description: product.description,
      shortDescription: product.shortDescription,
      currency: product.price?.final.currency,
      value: product.price?.final.value || 0,
    })),
  ].sort((a, b) => {
    const valueComparison = (a.value ?? 0) - (b.value ?? 0); // if value is same, sort by name
    return valueComparison === 0
      ? (a.name ?? '').localeCompare(b.name ?? '')
      : valueComparison;
  });
}
